import { displayError } from '../helpers';
import { getGeneralJournalDetails } from '../services/GeneralJournalServices';
import { getPlantDetails } from '../services/PlantServices';
import { getBgImageLogo } from '../services/LoginServices';
import { fetchImageDataUrl } from './pdfMedia';
import { createPdfBlob } from './PDFExporter';
import { openPdfInApp } from './openPdfInApp';

async function buildGeneralJournalPdfHeader(cookies, plantId) {
    let companyName = '';
    let addressLines = [];
    let gstNo = '';
    let logoPath = '';

    if (plantId) {
        try {
            const plant = await getPlantDetails(cookies, plantId);
            companyName = plant.entity_name || '';
            const line1 = [plant.address_1, plant.address_2].filter(Boolean).join(', ');
            const line2 = [plant.address_3, plant.state, plant.pincode].filter(Boolean).join(' - ');
            const contactParts = [];
            if (plant.phone_number || plant.mobile_number) {
                contactParts.push(`Cell: ${[plant.phone_number, plant.mobile_number].filter(Boolean).join(', ')}`);
            }
            if (plant.email_id) {
                contactParts.push(`Email: ${plant.email_id}`);
            }
            addressLines = [line1, line2, contactParts.join(', ')].filter(Boolean);
            gstNo = plant.gst_no || '';
            logoPath = plant.plant_logo || '';
        } catch (error) {
            // Fall back below.
        }
    }

    if (!companyName) {
        try {
            const response = await getBgImageLogo();
            const company = (response.company_list || []).find((item) => item.status)
                || response.company_list?.[0];
            if (company) {
                companyName = company.name || '';
                logoPath = logoPath || company.logo || '';
            }
        } catch (error) {
            // Header without logo.
        }
    }

    const logoDataUrl = logoPath
        ? await fetchImageDataUrl(cookies, logoPath).catch(() => null)
        : null;

    return {
        companyName,
        addressLines,
        gstNo,
        logoDataUrl,
        documentTitle: 'General Journal',
    };
}

export function fetchAndShowGeneralJournalPdf(cookies, journalId, options = {}) {
    return getGeneralJournalDetails(cookies, journalId)
        .then(async (record) => {
            const header = await buildGeneralJournalPdfHeader(cookies, record.plant_id);
            header.subtitle = [
                `Journal No: ${record.journal_no || ''}`,
                `Date: ${record.journal_date || ''}`,
                `Status: ${record.status ? 'Active' : 'Cancelled'}`,
            ].join('  |  ');

            const columns = [
                { header: '#', dataKey: 'slno', cellWidth: 10 },
                { header: 'Account', dataKey: 'account' },
                { header: 'Debit', dataKey: 'debit', align: 'right', halign: 'right', cellWidth: 28 },
                { header: 'Credit', dataKey: 'credit', align: 'right', halign: 'right', cellWidth: 28 },
                { header: 'Line Narration', dataKey: 'narration' },
            ];

            const rows = (record.journal_lines || []).map((line, index) => ({
                slno: String(index + 1),
                account: line.account_name || '',
                debit: line.debit_amt ? Number(line.debit_amt).toFixed(2) : '',
                credit: line.credit_amt ? Number(line.credit_amt).toFixed(2) : '',
                narration: line.line_narration || '',
            }));

            const summaryLines = [
                `Total Debit: ${Number(record.total_debit || 0).toFixed(2)}`,
                `Total Credit: ${Number(record.total_credit || 0).toFixed(2)}`,
            ];
            if (record.remarks) {
                summaryLines.push(`Narration / Remarks: ${record.remarks}`);
            }

            const blob = createPdfBlob('', columns, rows, summaryLines, header);
            openPdfInApp(blob, {
                title: options.title || 'General Journal',
                downloadFileName: options.downloadFileName
                    || `GeneralJournal_${String(record.journal_no || journalId).replace(/\//g, '_')}.pdf`,
            });
        })
        .catch((error) => {
            displayError(
                error?.response?.data || error,
                options.errorTitle || 'Print Failed',
            );
        });
}
